import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ArrowLeft, Sparkles, ExternalLink, Loader2 } from 'lucide-react';
import { DarkModeToggle } from '../components/DarkModeToggle';
import { settingsAPI, usersAPI, trialsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Tarification() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [packs, setPacks] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [centerSettings, setCenterSettings] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [processingPack, setProcessingPack] = useState(null);
  const [{ isPending }, paypalDispatch] = usePayPalScriptReducer();

  useEffect(() => {
    // Wait for auth to finish loading before fetching data
    if (authLoading) return;
    
    const loadData = async () => {
      setLoading(true);
      setPacks([]); // Reset packs on each load
      
      try {
        let loadedPacks;
        let loadedSettings;
        let centerId = null;
        
        // Priority 1: User is logged in and has a center
        if (user?.center_id) {
          centerId = user.center_id;
        } 
        // Priority 2: User is an admin
        else if (user?.role === 'admin') {
          centerId = user.id;
        }
        // Priority 3: Check for invitation code in URL
        else {
          const invitationCode = searchParams.get('code');
          if (invitationCode) {
            const adminUser = await usersAPI.getUserByInvitationCode(invitationCode);
            if (adminUser) {
              centerId = adminUser.id;
            }
          }
        }
        
        // Load data based on center
        if (centerId) {
          loadedPacks = await settingsAPI.getCenterPacks(centerId);
          loadedSettings = await settingsAPI.getCenterSettings(centerId);
          setCenterSettings(loadedSettings);
        } else {
          // No center - use global packs
          loadedPacks = await settingsAPI.getPacks();
          loadedSettings = await settingsAPI.getSiteSettings();
          setCenterSettings(null);
        }
        
        setPacks(loadedPacks || []);
        setSiteSettings(loadedSettings || {});

        // Load PayPal config from global site settings for online payment
        try {
          const globalSettings = await settingsAPI.getSiteSettings();
          if (globalSettings?.paypal_client_id) {
            setPaypalClientId(globalSettings.paypal_client_id);
            paypalDispatch({
              type: 'resetOptions',
              value: { 'client-id': globalSettings.paypal_client_id, currency: 'USD', intent: 'capture' },
            });
          }
        } catch {}
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user, searchParams, authLoading]);

  const handleSubscribe = (pack) => {
    const paymentLink = pack.payment_link || pack.paymentLink;
    if (paymentLink) {
      const url = paymentLink.startsWith('http://') || paymentLink.startsWith('https://')
        ? paymentLink
        : 'https://' + paymentLink;
      window.open(url, '_blank');
    } else if (!user) {
      navigate('/login');
    }
    // If logged in and pack uses PayPal, PayPal buttons below handle payment
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  // Go back to center landing page or dashboard
                  if (user) {
                    navigate('/dashboard');
                  } else if (centerSettings) {
                    // Get invitation code from URL or center settings
                    const code = searchParams.get('code');
                    navigate(code ? `/?code=${code}` : '/');
                  } else {
                    navigate('/');
                  }
                }}
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Retour</span>
              </button>
              <div className="h-6 w-px bg-border" />
              {centerSettings?.logo && (
                <img 
                  src={centerSettings.logo.startsWith('http') ? centerSettings.logo : `${API_URL}${centerSettings.logo.startsWith('/') ? '' : '/'}${centerSettings.logo}`} 
                  alt="Logo" 
                  className="h-8 w-auto" 
                />
              )}
              <h1 className="text-2xl font-bold text-primary">
                {centerSettings?.site_name || centerSettings?.siteName || siteSettings?.site_name || siteSettings?.siteName || 'TCF Canada'}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <DarkModeToggle />
              {user ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  data-testid="nav-dashboard-button"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
                >
                  Mon espace
                </button>
              ) : (
                <button
                  onClick={() => {
                    const code = searchParams.get('code');
                    navigate(code ? `/?code=${code}` : '/login');
                  }}
                  data-testid="nav-login-button"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
                >
                  Se connecter
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">Choisissez votre formule</span>
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4">Nos Tarifs</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Des formules adaptées à vos besoins pour réussir votre TCF Canada. 
            Accédez à tous nos contenus et outils de préparation.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packs.map((pack) => (
              <div
                key={pack.id}
                data-testid={`pricing-card-${pack.id}`}
                className={`relative bg-card p-8 rounded-2xl border-2 shadow-lg transition-all hover:shadow-xl ${
                  pack.popular
                    ? 'border-primary md:scale-105 lg:scale-110'
                    : 'border-transparent hover:border-primary/30'
                }`}
              >
                {pack.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary text-primary-foreground text-sm font-bold px-4 py-1 rounded-full">
                      POPULAIRE
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{pack.name}</h3>
                  <div className="flex items-baseline justify-center space-x-1">
                    <span className="text-4xl font-bold">${pack.price}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">Durée : {pack.duration}</p>
                </div>

                <div className="space-y-3 mb-8">
                  {pack.features.map((feature, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <div className="h-5 w-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-green-600" />
                      </div>
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-secondary/50 p-4 rounded-lg text-center mb-6">
                  <p className="font-semibold text-primary">
                    {pack.essais_ia || pack.essaisIA || 0} essais IA inclus
                  </p>
                </div>

                {/* S'abonner button — always visible and blue */}
                <button
                  onClick={() => handleSubscribe(pack)}
                  data-testid={`subscribe-btn-${pack.id}`}
                  className="w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <span>S'abonner</span>
                  {(pack.payment_link || pack.paymentLink) && <ExternalLink className="h-4 w-4" />}
                </button>

                {/* PayPal payment option for logged-in users — shown below S'abonner */}
                {user && (pack.essais_ia || 0) > 0 && paypalClientId && !isPending && (
                  <div className="mt-3 space-y-2">
                    {processingPack === pack.id && (
                      <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground py-2">
                        <Loader2 className="animate-spin h-4 w-4" />
                        <span>Traitement en cours...</span>
                      </div>
                    )}
                    <PayPalButtons
                      style={{ layout: 'vertical', shape: 'pill', label: 'pay' }}
                      createOrder={async (data, actions) => {
                        setProcessingPack(pack.id);
                        try {
                          const res = await trialsAPI.buyUserPack(user.id, pack.id);
                          if (!res.success) throw new Error(res.message);
                          return actions.order.create({
                            purchase_units: [{
                              amount: { currency_code: 'USD', value: String(pack.price || '0') },
                              description: `${pack.name} — TCF Canada Mon Combat`,
                              custom_id: res.data.purchase_id,
                            }],
                          });
                        } catch (err) {
                          setProcessingPack(null);
                          toast.error(err.message || 'Erreur lors de l\'initialisation du paiement');
                          throw err;
                        }
                      }}
                      onApprove={async (data, actions) => {
                        try {
                          const order = await actions.order.capture();
                          const purchaseId = order.purchase_units[0].custom_id;
                          const res = await trialsAPI.confirmUserPack(data.orderID, purchaseId);
                          if (res.success) {
                            toast.success(res.message || `${pack.essais_ia} essais ajoutés à votre compte !`);
                          } else {
                            toast.error(res.message || 'Erreur lors de la validation');
                          }
                        } catch (err) {
                          toast.error('Erreur lors de la validation du paiement');
                        } finally {
                          setProcessingPack(null);
                        }
                      }}
                      onError={() => {
                        setProcessingPack(null);
                        toast.error('Le service de paiement est temporairement indisponible.');
                      }}
                      onCancel={() => setProcessingPack(null)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-secondary/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Questions fréquentes</h2>
          <div className="space-y-4">
            <details className="bg-card p-6 rounded-xl border group">
              <summary className="font-semibold cursor-pointer">
                Comment puis-je payer ?
              </summary>
              <p className="mt-4 text-muted-foreground">
                En cliquant sur "S'abonner", vous serez redirigé vers notre page de paiement sécurisée. 
                Nous acceptons les cartes bancaires et autres moyens de paiement.
              </p>
            </details>
            <details className="bg-card p-6 rounded-xl border group">
              <summary className="font-semibold cursor-pointer">
                Puis-je changer de formule ?
              </summary>
              <p className="mt-4 text-muted-foreground">
                Oui, vous pouvez upgrader votre formule à tout moment. Contactez-nous pour plus d'informations.
              </p>
            </details>
            <details className="bg-card p-6 rounded-xl border group">
              <summary className="font-semibold cursor-pointer">
                Quels sont les essais IA ?
              </summary>
              <p className="mt-4 text-muted-foreground">
                Les essais IA vous permettent de soumettre vos textes (Expression Écrite) à notre 
                simulateur d'intelligence artificielle qui corrige et évalue vos productions.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t py-8 px-4">
        <div className="max-w-7xl mx-auto text-center text-sm text-muted-foreground">
          <p>© 2026 {siteSettings.siteName || 'TCF Canada'} - Tous droits réservés</p>
        </div>
      </footer>
    </div>
  );
}
